import { parseHotelDelivery } from '../lib/hotelDelivery';
import { parseCruiseReservation, formatCruiseSummary } from '../lib/cruiseReservation';
/**
 * プラン接頭マーク（日別一覧の時間列・スマホの予約カード・車両位置マップで共通）。
 * 予約データに印は持たず、プラン名（contact_plan）からその場で判定する。
 *
 *   🏪  駅近来店: 送迎なし。お客様が自分で店舗へ来る（2026-11-02 追加。レンナビ「…★駅近来店」）。
 *       セルフ（希望があれば送迎可・時間の約束なし）とは別で、送迎そのものをしない
 *   👤  代表者送迎: 代表者だけ送迎する（セルフではない）。
 *       例 じゃらん「【ファミリータイプ】…+無料送迎（代表者） 込」（2026-10-24 追加）
 *   🚶‍♀️ セルフ: お客様が自分で来店する
 *   🚃  ステーション
 *
 * 判定順: 駅近来店 → 代表者送迎 → セルフ → ステーション（送迎の有無がはっきり書かれたものを先に見る）。
 */
export const WALK_IN_PLAN_KEYWORD = '駅近来店';
export const WALK_IN_MARK = '🏪';

/** 駅近来店（送迎なし・店舗へ直接来店）のプランか */
export function isWalkInPlan(plan: string | null | undefined): boolean {
  return String(plan ?? '').includes(WALK_IN_PLAN_KEYWORD);
}

export const REPRESENTATIVE_PICKUP_PATTERN = /送迎[（(]代表者[）)]|代表者送迎/;

export function isRepresentativePickupPlan(plan: string | null | undefined): boolean {
  return REPRESENTATIVE_PICKUP_PATTERN.test(String(plan ?? ''));
}

export function planPrefixOf(plan: string | null | undefined): string {
  const p = String(plan ?? '');
  if (isWalkInPlan(p)) return WALK_IN_MARK;
  if (isRepresentativePickupPlan(p)) return '👤';
  if (p.includes('セルフ')) return '🚶‍♀️';
  if (p.includes('ステーション')) return '🚃';
  return '';
}

// ---------------------------------------------------------------------------
// ホテル配車の目印（2026-10-25。日別一覧・スマホのカード・車両位置マップで共通）
//   🏨 を planPrefix と同じ枠に出す。👤・🚶‍♀️・🚃 と重なったら 🏨 を優先して1つだけ（前田さん確認）。
//   貸出側: 配車区分 = 貸出場所まで配車
//   返却側: 配車返却先 = ホテル / その他、または 配車区分 = 返却のみ指定
//   配車場所 = 未入力（または空）なら placeMissing（🏨 の右上に赤い点。⚠ はショートメモの印なので使わない）
// 行・カードは濃い青緑の点線の枠（HOTEL_DELIVERY_OUTLINE）で目立たせる（車両位置マップは 🏨 だけ）。
// 正: booking-platform/md/hotel-delivery.md §5、データの読み出しは lib/hotelDelivery.ts
// ---------------------------------------------------------------------------

export const HOTEL_DELIVERY_MARK = '🏨';
export const HOTEL_DELIVERY_COLOR = '#115e59'; // 白地とのコントラスト 7.6:1
/** 行・カードを囲む点線（outline なので行の幅・高さは変わらない） */
export const HOTEL_DELIVERY_OUTLINE = { outline: `2px dashed ${HOTEL_DELIVERY_COLOR}`, outlineOffset: '-2px' } as const;

export interface HotelDeliveryMark {
  /** 配車場所が未入力（スタッフが場所を確かめる必要がある） */
  placeMissing: boolean;
  /** 表示用の説明（tooltip / トースト） */
  label: string;
}

/** この行（貸出 / 返却）がホテル配車の対象か。対象でなければ null */
export function hotelDeliveryMarkOf(jsonData: unknown, rowType: 'departure' | 'return'): HotelDeliveryMark | null {
  const h = parseHotelDelivery(jsonData);
  if (!h) return null;
  const applies = rowType === 'departure'
    ? !h.returnOnly
    : (h.returnOnly || h.returnPlace === 'ホテル' || h.returnPlace === 'その他');
  if (!applies) return null;
  const place = rowType === 'return' && !h.returnOnly && h.returnPlace === 'その他'
    ? (h.returnOther || '')
    : h.place;
  const placeMissing = !place || place === '未入力';
  const what = rowType === 'departure' ? 'ホテル配車（貸出）' : 'ホテル返却';
  return { placeMissing, label: `${what}: ${placeMissing ? '場所が未入力' : place}` };
}

// ---------------------------------------------------------------------------
// クルーズ船の目印（2026-11-02。日別一覧・スマホのカード・車両位置マップで共通）
//   🚢 を 🏨 と同じ枠に出す。貸出・返却とも港なので両方の行に出す。行・カードは濃い青の点線の枠（CRUISE_OUTLINE）。
//   判定は lib/cruiseReservation.ts parseCruiseReservation（船・プラン・クルーズ料金・事前決済のキー、古い予約は内訳コメント）。
//   🏨 と両方あるときは 🏨 を優先（マーク・枠とも）。港でのお渡しをやめてホテルまで配車するとスタッフが決めた予約なので、
//   行の目印は「どこへ車を持っていくか」を示す 🏨（と配車場所が未入力の赤い点）を残す。クルーズ船のことは tooltip に並べ、
//   サイドパネルには両方のセクションが出る。
// 正: booking-platform/md/pricing.md「那覇のクルーズ船プラン」・md/master_md/page-daylist.md
// ---------------------------------------------------------------------------

export const CRUISE_MARK = '🚢';
export const CRUISE_COLOR = '#1e40af'; // 白地とのコントラスト 8.7:1（🏨 の青緑と見分ける）
export const CRUISE_OUTLINE = { outline: `2px dashed ${CRUISE_COLOR}`, outlineOffset: '-2px' } as const;

export interface CruiseMark {
  /** 表示用の説明（tooltip / トースト） */
  label: string;
}

/** クルーズ船の予約か（貸出・返却とも同じ）。違えば null */
export function cruiseMarkOf(jsonData: unknown, comment?: string | null): CruiseMark | null {
  const c = parseCruiseReservation(jsonData, comment);
  return c ? { label: formatCruiseSummary(c) } : null;
}

/** 時刻の下の枠に出すマーク（🏨 優先 → 🚢 → 🏪 / 👤 / 🚶‍♀️ / 🚃） */
export function rowPrefixOf(plan: string | null | undefined, hotel: HotelDeliveryMark | null, cruise: CruiseMark | null = null): string {
  if (hotel) return HOTEL_DELIVERY_MARK;
  if (cruise) return CRUISE_MARK;
  return planPrefixOf(plan);
}

/** マークの説明（tooltip）。🏨 と 🚢 が両方あれば並べる */
export function rowPrefixTitleOf(plan: string | null | undefined, hotel: HotelDeliveryMark | null, cruise: CruiseMark | null): string {
  const parts = [hotel?.label, cruise?.label].filter(Boolean) as string[];
  return parts.length ? parts.join(' ／ ') : String(plan ?? '');
}

/** 行・カードの点線の枠（🏨 優先） */
export function rowOutlineOf(hotel: HotelDeliveryMark | null, cruise: CruiseMark | null): typeof HOTEL_DELIVERY_OUTLINE | typeof CRUISE_OUTLINE | Record<string, never> {
  if (hotel) return HOTEL_DELIVERY_OUTLINE;
  if (cruise) return CRUISE_OUTLINE;
  return {};
}
